import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { ValidateEmail, ValidateHourMinutes } from 'app/helpers/validators';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListAssistTypesConfigRes } from 'app/interfaces/responses/List/listAssistTypesConfigRes';
import { ConfigsService } from 'app/services/api/configs/configs.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { FormService } from 'app/services/system/form/form.service';
import moment from 'moment';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'app-settings-configs-assists',
    templateUrl: './settings-configs-assists.component.html',
    styleUrls: ['./settings-configs-assists.component.scss'],
})
export class SettingsConfigsAssistsComponent implements OnInit {
    //#region KEYPRESSES

    // ------------------- Leyfir notanda að vista á ctrl+s -----------------------
    onKeyDown($event): void {
        // Detect platform
        if (navigator.platform.match('Mac')) {
            this.handleMacKeyEvents($event);
        } else {
            this.handleWindowsKeyEvents($event);
        }
    }

    handleMacKeyEvents($event) {
        // MetaKey documentation
        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/metaKey
        let charCode = String.fromCharCode($event.which).toLowerCase();
        if ($event.metaKey && charCode === 's') {
            // Action on Cmd + S
            $event.preventDefault();
            this.save();
        }
    }

    handleWindowsKeyEvents($event) {
        let charCode = String.fromCharCode($event.which).toLowerCase();
        if ($event.ctrlKey && charCode === 's') {
            // Action on Ctrl + S
            $event.preventDefault();
            this.save();
        }
    }

    //#endregion

    //#region GLOBALS

    form: UntypedFormGroup;
    formEnabled = false;
    userDetails: ExposedReadUserDetailsRes = null;
    editorDayOpen = ClassicEditor;
    editorClosed = ClassicEditor;
    date = new Date();
    dayOpenTextHTML: string;
    closedTextHTML: string;

    listAssistTypes: Array<ListAssistTypesConfigRes>;
    assistTypes: Array<DropDownItem> = [];
    selectedAssistType: ListAssistTypesConfigRes;

    //#endregion

    //#region INIT

    /**
     * Constructor
     */
    constructor(
        public lsrauthService: LsrauthService,
        private _formBuilder: UntypedFormBuilder,
        private formService: FormService,
        private alertsService: AlertsService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _router: Router,
        private configsService: ConfigsService
    ) {
        this.formService.configAssistsForm.subscribe((configAssistsForm) => (this.form = configAssistsForm));
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.initForm('Innanhúss tölvuaðstoð');
    }

    // Makes sure to update form data to parent component whenever the form data changes.
    updateFormToParentComponent() {
        this.form.valueChanges.subscribe(() => {
            this.formService.changeConfigAssistsForm(this.form);
        });
    }

    public onChangeDayOpenText({ editor }: ChangeEvent) {
        this.form.markAsDirty();
        this.closedTextHTML = editor.getData();
        this.form.get('dayOpenTextHTML').setValue(editor.getData());
    }

    public onChangeClosedText({ editor }: ChangeEvent) {
        this.form.markAsDirty();
        this.dayOpenTextHTML = editor.getData();
        this.form.get('closedTextHTML').setValue(editor.getData());
    }

    dayOpenActiveTrigger(e) {
        this.dayOpenDisabledEnabled(e.checked);
    }

    dayOpenDisabledEnabled(enabled: boolean) {
        if (enabled) {
            this.form.get('dayOpenFrom').enable();
            this.form.get('dayOpenTo').enable();
            this.form.get('dayOpenTextHTML').enable();
            this.addDayOpenValidators();
        } else {
            this.form.get('dayOpenFrom').disable();
            this.form.get('dayOpenTo').disable();
            this.form.get('dayOpenTextHTML').disable();
            this.removeDayOpenValidators();
        }
    }

    closedActiveTrigger(e) {
        this.closedDisabledEnabled(e.checked);
    }

    closedDisabledEnabled(enabled: boolean) {
        if (enabled) {
            this.form.get('closedFrom').enable();
            this.form.get('closedTo').enable();
            this.form.get('closedTextHTML').enable();
            this.addClosedValidators();
        } else {
            this.form.get('closedFrom').disable();
            this.form.get('closedTo').disable();
            this.form.get('closedTextHTML').disable();
            this.removeClosedValidators();
        }
    }

    closedFromChange(e) {
        console.log(e);
    }

    cleanGlobals() {
        this.editorDayOpen = ClassicEditor;
        this.editorClosed = ClassicEditor;
        this.closedTextHTML = '';
        this.dayOpenTextHTML = '';
        this.listAssistTypes = [];
        this.assistTypes = [];
        try {
            this.form.reset();
        } catch {}
    }

    initForm(AssistTypeName) {
        this.cleanGlobals();
        this.formEnabled = false;
        let readAssists = this.configsService.readAssistsConfig({
            AssistTypeName: AssistTypeName,
        });
        let listAssistTypes = this.configsService.listAssistTypesConfig();
        combineLatest([readAssists, listAssistTypes]).subscribe(([readAssistRes, listAssistTypesRes]) => {
            this.listAssistTypes = listAssistTypesRes;
            listAssistTypesRes.forEach((x) => {
                this.assistTypes.push({
                    value: x.assistsTypesConfigId.toString(),
                    viewValue: x.name,
                });
            });

            this.selectedAssistType = listAssistTypesRes.find((x) => x.name == AssistTypeName);

            this.form = this._formBuilder.group({
                dayOpenFrom: new UntypedFormControl(readAssistRes?.dayOpenFrom ? moment(readAssistRes.dayOpenFrom).format('HH:mm') : ''),
                dayOpenTo: new UntypedFormControl(readAssistRes?.dayOpenTo ? moment(readAssistRes.dayOpenTo).format('HH:mm') : ''),
                dayOpenActive: new UntypedFormControl(readAssistRes?.dayOpenActive ? readAssistRes.dayOpenActive : false),
                dayOpenTextHTML: new UntypedFormControl(readAssistRes?.dayOpenTextHTML ? readAssistRes.dayOpenTextHTML : ''),
                closedFrom: new UntypedFormControl(readAssistRes?.closedFrom ? readAssistRes.closedFrom : null),
                closedTo: new UntypedFormControl(readAssistRes?.closedTo ? readAssistRes.closedTo : null),
                closedActive: new UntypedFormControl(readAssistRes?.closedActive ? readAssistRes.closedActive : false),
                closedTextHTML: new UntypedFormControl(readAssistRes?.closedTextHTML ? readAssistRes.closedTextHTML : ''),
                assistTypesConfigId: [this.selectedAssistType.assistsTypesConfigId.toString(), [Validators.required]],
                assignedToEmail: [readAssistRes?.assignedToEmail, [Validators.required, ValidateEmail]],
                tags: new UntypedFormControl(readAssistRes?.tags),
            });

            this.closedTextHTML = readAssistRes?.closedTextHTML ? readAssistRes.closedTextHTML : '';
            this.dayOpenTextHTML = readAssistRes?.dayOpenTextHTML ? readAssistRes.dayOpenTextHTML : '';

            if (readAssistRes?.dayOpenActive) {
                this.addDayOpenValidators();
            }
            if (readAssistRes?.closedActive) {
                this.addClosedValidators();
            }

            this.updateFormToParentComponent();
            this.formEnabled = true;

            this.dayOpenDisabledEnabled(readAssistRes?.dayOpenActive ? readAssistRes.dayOpenActive : false);
            this.closedDisabledEnabled(readAssistRes?.closedActive ? readAssistRes.closedActive : false);
        });
    }

    //#endregion

    //#region FORM AND SAVE

    public editorConfig: any = {
        alignment: { options: ['left', 'right', 'center', 'justify'] },
        toolbar: ['heading', '|', 'bold', 'italic', 'bulletedList', 'numberedList', 'link', '|', 'undo', 'redo'],
    };

    onChangeAssistType(assistsTypesConfigId) {
        const dirtyFields = Object.entries(this.form.controls).filter((value) => value[1].dirty);
        if (dirtyFields.length > 1) {
            if (confirm('Þú ert með óvistaðar breytingar. Ertu viss um að þú viljir halda áfram?')) {
                this.proceedChangeAssistType(assistsTypesConfigId);
            } else {
                this.form.get('assistTypesConfigId').setValue(this.selectedAssistType.assistsTypesConfigId.toString());
            }
        } else {
            this.proceedChangeAssistType(assistsTypesConfigId);
        }
    }

    proceedChangeAssistType(assistsTypesConfigId) {
        this.form.get('assistTypesConfigId').setValue(assistsTypesConfigId);
        this.selectedAssistType = this.listAssistTypes.find((x) => x.assistsTypesConfigId == assistsTypesConfigId);
        this.initForm(this.listAssistTypes.find((x) => x.assistsTypesConfigId == assistsTypesConfigId).name);
    }

    addDayOpenValidators() {
        this.form.get('dayOpenFrom').setValidators([ValidateHourMinutes, Validators.required]);
        this.form.get('dayOpenFrom').updateValueAndValidity();
        this.form.get('dayOpenTo').setValidators([ValidateHourMinutes, Validators.required]);
        this.form.get('dayOpenTo').updateValueAndValidity();
    }

    removeDayOpenValidators() {
        this.form.get('dayOpenFrom').clearValidators();
        this.form.get('dayOpenFrom').updateValueAndValidity();
        this.form.get('dayOpenTo').clearValidators();
        this.form.get('dayOpenTo').updateValueAndValidity();
    }

    addClosedValidators() {
        this.form.get('closedFrom').setValidators([Validators.required]);
        this.form.get('closedFrom').updateValueAndValidity();
        this.form.get('closedTo').setValidators([Validators.required]);
        this.form.get('closedTo').updateValueAndValidity();
    }

    removeClosedValidators() {
        this.form.get('closedFrom').clearValidators();
        this.form.get('closedFrom').updateValueAndValidity();
        this.form.get('closedTo').clearValidators();
        this.form.get('closedTo').updateValueAndValidity();
    }

    revertAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við þær breytingar sem þú hefur gert?')) {
            this.form.markAsPristine();
            this.initForm('Innanhúss tölvuaðstoð');
        }
    }

    save() {
        this.form.markAllAsTouched();
        // Return if the form is invalid
        if (this.form.invalid) {
            this.alertsService.changealertHeader('Villur í formi');
            this.alertsService.changeAlertErrorMessage('Vinsamlegast leysið úr villum í formi');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        this.formEnabled = false;

        // Kannar hvort að opnunartími sé rétt skilgreindur
        let _dateOpenFrom = new Date();
        let _dateOpenTo = new Date();
        try {
            _dateOpenFrom.setHours(this.form.get('dayOpenFrom').value.slice(0, 2), this.form.get('dayOpenFrom').value.slice(3, 5));
            _dateOpenTo.setHours(this.form.get('dayOpenTo').value.slice(0, 2), this.form.get('dayOpenTo').value.slice(3, 5));
        } catch (e) {
            _dateOpenFrom = null;
            _dateOpenTo = null;
        }
        if (_dateOpenFrom > _dateOpenTo) {
            this.formEnabled = true;
            this.alertsService.changealertHeader('Opnunartími');
            this.alertsService.changeAlertErrorMessage('<strong>Opið frá</strong> þarf að gerast á undan <strong>opið til</strong>');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        // Kannar hvort lokunartími sé rétt skilgreindur
        if (this.form.get('closedFrom').value > this.form.get('closedTo').value) {
            this.formEnabled = true;
            this.alertsService.changealertHeader('Lokunartími');
            this.alertsService.changeAlertErrorMessage('<strong>Lokað frá</strong> þarf að gerast á undan <strong>lokað til</strong>');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        this.configsService
            .updateAssistsConfig({
                dayOpenFrom: _dateOpenFrom,
                dayOpenTo: _dateOpenTo,
                dayOpenActive: this.form.get('dayOpenActive').value,
                dayOpenTextHTML: this.form.get('dayOpenTextHTML').value,
                closedFrom: this.form.get('closedFrom').value,
                closedTo: this.form.get('closedTo').value,
                closedActive: this.form.get('closedActive').value,
                closedTextHTML: this.form.get('closedTextHTML').value,
                assistsTypesConfigId: this.form.get('assistTypesConfigId').value,
                assignedToEmail: this.form.get('assignedToEmail').value,
                tags: this.form.get('tags').value,
            })
            .subscribe((res) => {
                this.closedTextHTML = this.form.get('closedTextHTML').value;
                this.dayOpenTextHTML = this.form.get('dayOpenTextHTML').value;
                this.form.markAsPristine();
                this.formEnabled = true;
            });
    }

    //#endregion
}
